import { useEffect } from 'react';
import { MenuContainer, MenuItem } from './styles';

interface SubMenuProps {
  anchorEl: null | HTMLElement;
  setAnchorEl: (el: null | HTMLElement) => void;
  id: number;
  isOpen: boolean;
  items: any[];
  closeMenu: () => void;
  onClickItem?: (path: string) => void;
}

export const SubMenu = ({ anchorEl, setAnchorEl, isOpen, items, closeMenu, onClickItem, id }: SubMenuProps) => {
  const handleClick = (path: string) => {
    if (path) {
      if (onClickItem) onClickItem(path);
      closeMenu();
      setAnchorEl(null);
    }
  };

  const handleClose = () => {
    closeMenu();
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, anchorEl]);

  if (!isOpen || !anchorEl) return null;

  const anchorRect = anchorEl.getBoundingClientRect();
  const padding = 10;

  const calculatePosition = () => {
    const spaceBelow = window.innerHeight - anchorRect.bottom - padding;
    const spaceAbove = anchorRect.top - padding;
    const totalAvailableSpace = spaceBelow + spaceAbove;

    const heightOfMenu = items.length * 40;
    const widthOfMenu = 200;

    let top;
    let maxHeight;

    if (heightOfMenu <= totalAvailableSpace) {
      top = spaceBelow >= heightOfMenu ? anchorRect.bottom : anchorRect.bottom - (heightOfMenu - spaceBelow);
      maxHeight = heightOfMenu;
    } else {
      top = spaceBelow > spaceAbove ? anchorRect.bottom : anchorRect.top - spaceAbove;
      maxHeight = spaceBelow > spaceAbove ? spaceBelow : spaceAbove;
    }

    maxHeight = Math.max(maxHeight, 0);

    if (anchorRect.left + widthOfMenu > window.innerWidth) {
      return {
        top,
        left: anchorRect.left - widthOfMenu,
        maxHeight,
      };
    }

    return { top, left: anchorRect.left, maxHeight };
  };

  const { top, left, maxHeight } = calculatePosition();

  return (
    <MenuContainer
      id={String(id ?? 0)}
      style={{
        top: top,
        left: left,
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
      }}
    >
      {items.map(item => (
        <MenuItem key={item.label} onClick={() => handleClick(item.path || '')}>
          {item.icon ? <item.icon /> : <></>}
          {item.label}
        </MenuItem>
      ))}
    </MenuContainer>
  );
};
